import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import createMetadata from '@/shared/api/product/createMetadata';
import createProductVariant from '@/shared/api/product/createProductVariant';
import updateProduct from '@/shared/api/product/updateProduct';
import updateProductVariants from '@/shared/api/product/updateProductVariants';
import getFormFieldsToSave from '@/shared/components/widgets/metadata/CreateMetadataForm/utils/getFormFieldsToSave';
import transformMetadataForm from '@/shared/hooks/useMetadataForm/utils/transformMetadataForm';
import useModalDialog from '@/shared/hooks/useModalDialog';
import useNotificationBar from '@/shared/hooks/useNotificationBar';
import useProgressDialog from '@/shared/hooks/useProgressDialog';
import useRedirectLogin from '@/shared/hooks/useRedirectLogin';
import DialogName from '@/shared/types/dialogName.enum';
import LevelConstraint from '@/shared/types/metadata/levelConstraint.type';
import Product from '@/shared/types/products/product.type';
import ProductVariant from '@/shared/types/products/productVariant.type';

import MetadataTransfer, {
  MetadataTransferList,
} from '../types/metadataTransfer.type';

type Args = {
  productId: string;
};

// Todo: Clean up required for 'useProductOperations' hook
const useProductOperations = ({ productId }: Args) => {
  const redirectLogin = useRedirectLogin();
  const { t } = useTranslation(['products', 'common']);

  const [dataCollection, setDataCollection] = useState<{
    [LevelConstraint.PRODUCT]: MetadataTransfer;
    [LevelConstraint.VARIANT]: MetadataTransferList;
  }>({
    [LevelConstraint.PRODUCT]: {
      metadata: {},
      newMetadataDefinitions: [],
      isValid: false,
    },
    [LevelConstraint.VARIANT]: {
      metadata: [],
      newMetadataDefinitions: [],
      isDirty: false,
      isValid: false,
    },
  });

  const [shouldRevalidateForm, setShouldRevalidateForm] = useState(false);

  const { dialog, onUpdateDialogState } = useModalDialog<unknown, DialogName>();
  const { progressDialog, onUpdateProgressDialog } = useProgressDialog();
  const { notificationBar, onUpdateNotificationBar } = useNotificationBar();

  const handleCreateVariants = useCallback(
    async (variantsToCreate: Record<string, unknown>[]) => {
      onUpdateProgressDialog({
        open: true,
        progress: 0,
        title: t('products:creatingProductVariants'),
      });

      const responses = await createProductVariant({
        productId: productId,
        body: variantsToCreate,
        onUnauthorized: () => redirectLogin(),
      });

      if (responses.every((response) => response.success)) {
        onUpdateProgressDialog({ open: false });
        onUpdateNotificationBar({
          open: true,
          message: t('products:productVariantsCreated'),
          severity: 'success',
        });
      } else {
        onUpdateProgressDialog({ open: false });
        onUpdateDialogState(DialogName.UpdateProductVariants, {
          open: true,
          errors: responses
            .filter((response) => !response.success)
            .map((response) => response.msg),
        });
      }
    },
    [
      onUpdateDialogState,
      onUpdateNotificationBar,
      onUpdateProgressDialog,
      productId,
      redirectLogin,
      t,
    ],
  );

  const handleUpdateVariants = useCallback(async () => {
    onUpdateProgressDialog({
      open: true,
      progress: 0,
      title: t('products:updatingProductVariants'),
    });

    setDataCollection((prevState) => ({
      ...prevState,
      [LevelConstraint.VARIANT]: {
        ...prevState[LevelConstraint.VARIANT],
        isDirty: false,
      },
    }));

    const variantsToUpdate = dataCollection[LevelConstraint.VARIANT].metadata
      .map((metadata) => transformMetadataForm(metadata))
      .filter((metadata) => metadata.variant_id);

    const variantsToCreate = dataCollection[LevelConstraint.VARIANT].metadata
      .map((metadata) => transformMetadataForm(metadata))
      .filter((metadata) => !metadata.variant_id);

    if (variantsToUpdate.length === 0) {
      onUpdateProgressDialog({ open: false });
      onUpdateNotificationBar({
        open: true,
        message: t('products:productUpdated'),
        severity: 'success',
      });
      return;
    }

    const responses = await updateProductVariants({
      productId,
      body: variantsToUpdate,
    });

    if (responses.every((response) => response.success)) {
      if (variantsToCreate.length === 0) {
        onUpdateProgressDialog({ open: false });
        onUpdateNotificationBar({
          open: true,
          message: t('products:productVariantsUpdated'),
          severity: 'success',
        });
        return;
      }

      await handleCreateVariants(variantsToCreate);
    } else {
      onUpdateProgressDialog({ open: false });
      onUpdateDialogState(DialogName.UpdateProductVariants, {
        open: true,
        errors: responses
          .filter((response) => !response.success)
          .map((response) => response.msg),
      });
    }
  }, [
    dataCollection,
    handleCreateVariants,
    onUpdateDialogState,
    onUpdateNotificationBar,
    onUpdateProgressDialog,
    productId,
    t,
  ]);

  const handleUpdateProduct = useCallback(async () => {
    onUpdateProgressDialog({
      open: true,
      progress: 0,
      title: t('products:updatingProduct'),
    });

    const response = await updateProduct(
      productId,
      transformMetadataForm(dataCollection[LevelConstraint.PRODUCT].metadata),
    );

    if (response.success) {
      onUpdateProgressDialog({ open: false });
      onUpdateNotificationBar({
        open: true,
        message: t('products:productUpdated'),
        severity: 'success',
      });
      setDataCollection((prevState) => ({
        ...prevState,
        [LevelConstraint.PRODUCT]: {
          ...prevState[LevelConstraint.PRODUCT],
          isDirty: false,
        },
      }));
    } else {
      onUpdateProgressDialog({ open: false });
      onUpdateDialogState(DialogName.UpdateProduct, {
        open: true,
        errors: [response.msg],
      });
    }
  }, [
    dataCollection,
    onUpdateDialogState,
    onUpdateNotificationBar,
    onUpdateProgressDialog,
    productId,
    t,
  ]);

  const onCreateNewMetadata = async (
    formList: ReturnType<typeof getFormFieldsToSave>[],
  ) => {
    onUpdateDialogState(DialogName.CreateMetadata, { loading: true });
    onUpdateProgressDialog({
      open: true,
      progress: 0,
      title: t('products:creatingMetadata'),
    });

    const responses = await createMetadata(formList, redirectLogin);

    if (responses.every((response) => response.success)) {
      onUpdateProgressDialog({ open: false });
      onUpdateDialogState(DialogName.CreateMetadata, { open: false });
      onUpdateNotificationBar({
        open: true,
        message: t('products:metadataCreated'),
        severity: 'success',
      });
    } else {
      onUpdateProgressDialog({ open: false });
      onUpdateDialogState(DialogName.CreateMetadata, {
        open: true,
        errors: responses
          .filter((response) => !response.success)
          .map((response) => response.msg),
      });
    }
  };

  const onProductUpdate = async (details: Partial<Product>) => {
    onUpdateProgressDialog({
      open: true,
      progress: 0,
      title: t('products:updatingProduct'),
    });

    const response = await updateProduct(productId, details);

    if (response.success) {
      onUpdateProgressDialog({ open: false });
      onUpdateNotificationBar({
        open: true,
        message: t('products:productUpdated'),
        severity: 'success',
      });
    } else {
      onUpdateProgressDialog({ open: false });
      onUpdateDialogState(DialogName.UpdateProduct, {
        open: true,
        errors: [response.msg],
      });
    }
  };

  const onProductVariantsUpdate = async (
    details: Partial<ProductVariant>[],
  ) => {
    onUpdateProgressDialog({
      open: true,
      progress: 0,
      title: t('products:updatingProductVariants'),
    });

    const responses = await updateProductVariants({
      productId,
      body: details,
    });

    onUpdateProgressDialog({ open: false });

    if (responses.every((response) => response.success)) {
      onUpdateNotificationBar({
        open: true,
        message: t('products:productVariantsUpdated'),
        severity: 'success',
      });
    } else {
      onUpdateDialogState(DialogName.UpdateProductVariants, {
        open: true,
        errors: responses
          .filter((response) => !response.success)
          .map((response) => response.msg),
      });
    }
  };

  const onProductVariantsCreate = async (
    details: Record<string, unknown>[],
  ) => {
    onUpdateProgressDialog({
      open: true,
      progress: 0,
      title: t('products:creatingProductVariants'),
    });

    const responses = await createProductVariant({
      productId: productId,
      body: details,
      onUnauthorized: redirectLogin,
    });

    onUpdateProgressDialog({ open: false });

    if (responses.every((response) => response.success)) {
      onUpdateNotificationBar({
        open: true,
        message: t('products:productVariantsCreated'),
        severity: 'success',
      });
    } else {
      onUpdateDialogState(DialogName.UpdateProductVariants, {
        open: true,
        errors: responses
          .filter((response) => !response.success)
          .map((response) => response.msg),
      });
    }
  };

  useEffect(() => {
    if (shouldRevalidateForm) {
      setShouldRevalidateForm(false);

      // TODO: fix validation logic, commented for now to allow user to save the form
      // const isFormValid = [
      //   dataCollection[LevelConstraint.PRODUCT].isValid,
      //   dataCollection[LevelConstraint.VARIANT].isValid,
      // ].every((isValid) => isValid);

      // if (!isFormValid) {
      //   onUpdateNotificationBar({
      //     open: true,
      //     message: t('products:theFormIsNotValid'),
      //     severity: 'warning',
      //   });
      //   return;
      // }

      if (dataCollection[LevelConstraint.PRODUCT].isDirty) {
        void handleUpdateProduct();
      }

      if (dataCollection[LevelConstraint.VARIANT].isDirty) {
        void handleUpdateVariants();
      }
    }
  }, [
    dataCollection,
    shouldRevalidateForm,
    handleUpdateProduct,
    handleUpdateVariants,
  ]);

  return {
    dialog,
    progressDialog,
    notificationBar,
    setDataCollection,
    setShouldRevalidateForm,
    onUpdateDialogState,
    onUpdateNotificationBar,
    onUpdateProgressDialog,
    onCreateNewMetadata,
    // Todo: check returned items above and remove if not used
    onProductUpdate,
    onProductVariantsUpdate,
    onProductVariantsCreate,
  };
};

export default useProductOperations;
